import useDebouncedValue from 'hooks/useDebouncedValue';
import React, { useRef, useState } from 'react';
import { Grid, Typography, IconButton, Button, TextField, Tabs, Tab, Tooltip, InputAdornment, Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SearchIcon from '@material-ui/icons/Search';
import variables from '../../../../Sys/variable.scss';

const SEARCH_THRESHOLD = 5;

const useStyles = makeStyles((theme) => ({
    panel: {
        border: '1px solid #D6D6D6',
        borderRadius: 5,
        backgroundColor: variables.headerColor,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    tabs: {
        borderBottom: '1px solid #D6D6D6',
        minHeight: 44,
        width: '100%',
        maxWidth: '100%',
        '& .MuiTabs-scroller': {
            minWidth: 0,
        },
        [theme.breakpoints.down('xs')]: {
            '& .MuiTabScrollButton-root': {
                display: 'flex',
                width: 28,
            },
        },
    },
    tab: {
        minHeight: 44,
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 600,
        minWidth: 140,
        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
            fontSize: 13,
            paddingLeft: 10,
            paddingRight: 10,
        },
    },
    tabCount: {
        display: 'inline-block',
        minWidth: 22,
        padding: '1px 7px',
        marginLeft: 8,
        borderRadius: 11,
        fontSize: 12,
        lineHeight: '18px',
        background: '#E8EEF9',
        color: '#3D7DDD',
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid #EEEEEE',
        minWidth: 0,
        [theme.breakpoints.down('xs')]: {
            padding: '12px',
        },
    },
    panelHeading: {
        minWidth: 0,
    },
    panelTitle: {
        fontSize: 15,
        fontWeight: 700,
        color: variables.darkThemeText,
    },
    panelSubtitle: {
        fontSize: 12,
        color: theme.palette.grey[600],
    },
    panelActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minWidth: 0,
        maxWidth: '100%',
        [theme.breakpoints.down('sm')]: {
            flex: '1 1 220px',
            flexWrap: 'wrap',
        },
    },
    searchInput: {
        width: 220,
        maxWidth: '100%',
        [theme.breakpoints.down('sm')]: {
            flex: '1 1 160px',
            width: 'auto',
            minWidth: 0,
        },
    },
    addButton: {
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
    },
    courseLicenceGrid: {
        [theme.breakpoints.up('md')]: {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1.45fr) minmax(0, 1.35fr) minmax(96px, 0.72fr) 84px 36px',
            columnGap: 8,
            width: '100% !important',
            margin: '0 !important',
            '& > .MuiGrid-item': {
                maxWidth: 'none',
                flexBasis: 'auto',
                width: 'auto',
                padding: '4px 0 !important',
            },
            '& .MuiInputLabel-outlined': {
                fontSize: '0.8125rem',
                maxWidth: 'calc(100% - 32px)',
            },
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                maxWidth: 'calc(133% - 24px)',
            },
        },
    },
    columnHeader: {
        margin: 0,
        width: '100%',
        padding: '8px 8px',
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: theme.palette.grey[600],
        borderBottom: '1px solid #EEEEEE',
        backgroundColor: '#FAFAFA',
    },
    list: {
        maxHeight: 'calc(100vh - 420px)',
        minHeight: 180,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    row: {
        margin: 0,
        width: '100%',
        padding: '6px 8px',
        borderBottom: '1px solid #EEEEEE',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: '#FAFCFF',
        },
    },
    allocatedLabel: {
        fontSize: 11,
        color: theme.palette.grey[600],
    },
    allocatedValue: {
        fontSize: 14,
        fontWeight: 600,
        color: variables.darkThemeText,
    },
    actionCell: {
        textAlign: 'right',
    },
    deleteBtn: {
        color: '#E23A3A',
    },
    emptyState: {
        padding: theme.spacing(5, 2),
        textAlign: 'center',
        color: theme.palette.grey[600],
    },
}));

export const LicenceRow = ({ children, className }) => {
    const classes = useStyles();
    return (
        <Grid container spacing={2} alignItems="center" className={`${classes.row} licenceRow ${className || ''}`}>
            {children}
        </Grid>
    );
};

export const AllocatedCell = ({ count, total }) => {
    const classes = useStyles();
    return (
        <div>
            <Hidden mdUp>
                <div className={classes.allocatedLabel}>Allocated</div>
            </Hidden>
            <div className={classes.allocatedValue}>{count || 0} / {total || 0}</div>
        </div>
    );
};

export const RowDeleteButton = ({ action, index, handleClick, label, hidden }) => {
    const classes = useStyles();
    if (hidden) {
        return <Grid item xs={2} md={1} />;
    }
    return (
        <Grid item xs={2} md={1} className={classes.actionCell}>
            <Tooltip title={`Remove ${label}`}>
                <IconButton
                    size="small"
                    className={classes.deleteBtn}
                    aria-label={`remove ${action}`}
                    onClick={() => handleClick(action, index)}
                >
                    <DeleteOutlineIcon />
                </IconButton>
            </Tooltip>
        </Grid>
    );
};

/**
 * tabs: [{ key, label, singular, columns: [[label, mdSize]], canAdd, addDisabled, hideUnselected, subtitle, emptyMessage }]
 * items: { [tab.key]: rows[] }
 */
const LicenceTabsPanel = ({ tabs, items, isRowSelected, getRowLabel, renderRow, onAdd }) => {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(0);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search);
    const listRef = useRef(null);

    const getSelectedCount = (key) => (items[key] || []).filter(item => isRowSelected(key, item)).length;

    const handleAdd = (key) => {
        onAdd(key);
        setSearch('');
        setTimeout(() => {
            if (listRef.current) {
                listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
            }
        }, 0);
    };

    const tab = tabs[activeTab];
    const rows = items[tab.key] || [];
    const selectedCount = getSelectedCount(tab.key);
    const term = debouncedSearch.trim().toLowerCase();
    const visibleRows = rows
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !tab.hideUnselected || isRowSelected(tab.key, item))
        .filter(({ item }) => !term || getRowLabel(tab.key, item).toLowerCase().includes(term));
    const pluralLabel = tab.label.toLowerCase();

    return (
        <div className={classes.panel}>
            <Tabs
                value={activeTab}
                onChange={(evt, value) => {
                    setActiveTab(value);
                    setSearch('');
                }}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                className={classes.tabs}
            >
                {tabs.map(t => (
                    <Tab
                        key={t.key}
                        className={classes.tab}
                        label={
                            <span>
                                {t.label}
                                <span className={classes.tabCount}>{getSelectedCount(t.key)}</span>
                            </span>
                        }
                    />
                ))}
            </Tabs>
            <div className={classes.panelHeader}>
                <div className={classes.panelHeading}>
                    <Typography className={classes.panelTitle}>{tab.label}</Typography>
                    <Typography className={classes.panelSubtitle}>
                        {tab.subtitle || `${selectedCount} ${pluralLabel} selected`}
                    </Typography>
                </div>
                <div className={classes.panelActions}>
                    {rows.length > SEARCH_THRESHOLD && (
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder={`Search ${pluralLabel}`}
                            value={search}
                            onChange={(evt) => setSearch(evt.target.value)}
                            className={classes.searchInput}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    {tab.canAdd && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            className={classes.addButton}
                            startIcon={<AddIcon />}
                            disabled={!!tab.addDisabled}
                            onClick={() => handleAdd(tab.key)}
                        >
                            Add {tab.singular}
                        </Button>
                    )}
                </div>
            </div>
            <Hidden smDown>
                <Grid container spacing={2} className={`${classes.columnHeader} ${tab.alignCourses ? classes.courseLicenceGrid : ''}`}>
                    {tab.columns.map(([label, size], idx) => (
                        <Grid item md={size} key={`${tab.key}-col-${idx}`}>{label}</Grid>
                    ))}
                </Grid>
            </Hidden>
            <div className={classes.list} ref={listRef}>
                {visibleRows.length ? (
                    visibleRows.map(({ item, index }) => (
                        <React.Fragment key={`${tab.key}-${index}`}>
                            {tab.alignCourses
                                ? React.cloneElement(renderRow(tab.key, item, index), { className: classes.courseLicenceGrid })
                                : renderRow(tab.key, item, index)}
                        </React.Fragment>
                    ))
                ) : (
                    <div className={classes.emptyState}>
                        {term
                            ? `No ${pluralLabel} match "${search}".`
                            : (tab.emptyMessage || `No ${pluralLabel} added yet.`)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LicenceTabsPanel;
